import React, { useState, useEffect, useMemo } from "react";
import closeIcon from "../../../../../../../../Assets/images/close-icon.svg";
import addPlus from "../../../../../../../../Assets/images/add-plus.svg";
import minusIcon from "../../../../../../../../Assets/images/minus-icon.svg";
import equalIcon from "../../../../../../../../Assets/images/equal-icon.svg";
import { FIELD_MAX_LENGTHS } from "../../../../../Utils/Constants";
import apiClient from "../../../../../../../../Config/apiClient";
import { ClipLoader } from "react-spinners";
import { showErrorToast, showSuccessToast } from "../../../../../../../../Context/Toaster/Toaster";
import Loader from "../../../../../../../../Context/Loader/Loader";
import "./AdjustTons.scss";

const adjustUrl = process.env.REACT_APP_CC_ADJUSTMENT_BASE_PATH;

const AdjustTons = ({ show, onClose, truckJobId, jobData, truckDetails, initialWorkDone, onAdjusted }) => {
  const [adjustmentValue, setAdjustmentValue] = useState("");
  const [adjustmentType, setAdjustmentType] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isPrefillLoading, setIsPrefillLoading] = useState(true);

  const unitLabel = useMemo(() => {
    const type = jobData?.payment_type;
    if (type === 2 || type === "Hours" || type === "Hour") return "Hours";
    if (type === 3 || type === "Loads" || type === "Load") return "Loads";
    return "Tons";
  }, [jobData?.payment_type]);

  const finalWorkDone = useMemo(() => {
    const base = parseFloat(initialWorkDone || 0);
    const numeric = parseFloat(adjustmentValue);

    if (isNaN(numeric)) return base.toFixed(2);
    const result = adjustmentType === 1 ? base + numeric : base - numeric;
    return parseFloat(result.toFixed(2));
  }, [adjustmentValue, adjustmentType, initialWorkDone]);

  useEffect(() => {
    if (show) {
      setAdjustmentValue("");
      setAdjustmentType(1);
      setIsPrefillLoading(false);
    }
  }, [show]);

  const handleAdjustmentChange = (e) => {
    const val = e.target.value;
    if (!isNaN(val) || val === "") {
      setAdjustmentValue(val);
    }
  };

  const handleSubmit = async () => {
    if (!adjustmentValue || isNaN(adjustmentValue)) {
      showErrorToast(`Enter a valid ${unitLabel.toLowerCase()} to adjust`);
      return;
    }

    const numericValue = parseFloat(adjustmentValue);
    const baseValue = parseFloat(initialWorkDone || 0);
    if (numericValue === 0) {
      showErrorToast(`Adjustment value must be greater than zero`);
      return;
    }

    if (adjustmentType === 0 && numericValue > baseValue) {
      showErrorToast(`You cannot reduce more than ${baseValue} ${unitLabel}`);
      return;
    }

    try {
      setIsLoading(true);
      const signedValue = adjustmentType === 1 ? numericValue : -numericValue;

      const response = await apiClient.post(`${adjustUrl}/work-done`, {
        truckJobId,
        adjustment: signedValue,
        adjustmentType,
      });

      if (response?.data?.status === "SUCCESS") {
        showSuccessToast(`${unitLabel} adjusted successfully`);
        if (onAdjusted) onAdjusted();
        onClose();
      } else {
        showErrorToast(response?.data?.message || "Failed to adjust");
      }
    } catch (err) {
      console.error(err);
      showErrorToast("Unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal-backdrop-custom" style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}>
      <div className="modal fade show d-block" tabIndex="-1" aria-hidden="false">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="ajob-content">
              <div className="modal-top">
                <h2 className="modal-heading">Adjust {unitLabel}</h2>
                <button className="m-close" onClick={onClose}>
                  <img src={closeIcon} alt="Close" />
                </button>
              </div>

              <div className="cprofile-inner">
                <div className="cp-form" style={{ position: "relative", minHeight: "350px" }}>
                  {isPrefillLoading && (
                    <div
                      className="d-flex justify-content-center align-items-center w-100 h-100"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        background: "rgba(255, 255, 255, 0.6)",
                        zIndex: 10,
                      }}
                    >
                      <ClipLoader size={35} color="#59A52C" />
                    </div>
                  )}

                  <div
                    style={{
                      opacity: isPrefillLoading ? 0.4 : 1,
                      pointerEvents: isPrefillLoading ? "none" : "auto",
                    }}
                  >
                    <div className="row">
                      <div className="col-md-12">
                        <div className="cp-split">
                          <h3 className="modal-subhead">Truck</h3>
                          <select className="common-select mt-1" disabled>
                            <option>{truckDetails?.truckId || "N/A"}</option>
                          </select>
                        </div>
                      </div>

                      <div className="at-wraper">
                        <div className="row">
                          <div className="col-md-6">
                            <div className="modal-left-split">
                              <h3 className="modal-subhead">Vehicle Number</h3>
                              <p className="modal-value">{truckDetails?.vehicleNumber || "-"}</p>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="modal-left-split">
                              <h3 className="modal-subhead">License Plate Number</h3>
                              <p className="modal-value">{truckDetails?.licensePlate || "-"}</p>
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-6">
                            <div className="modal-left-split">
                              <h3 className="modal-subhead">Truck Type</h3>
                              <p className="modal-value">{truckDetails?.truckType || "-"}</p>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="modal-left-split">
                              <h3 className="modal-subhead">Work Done So Far</h3>
                              <p className="modal-value">
                                {parseFloat(initialWorkDone || 0).toFixed(2)} {unitLabel}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="at-adjust">
                          <h3 className="modal-subhead mb-0">Adjust {unitLabel} (+/-)</h3>
                          <div className="adjust-split">
                            <input
                              type="text"
                              value={parseFloat(initialWorkDone || 0).toFixed(2)}
                              disabled
                            />
                            <button
                              className={`plus-button ${adjustmentType === 1 ? "active" : ""}`}
                              onClick={() => setAdjustmentType(1)}
                            >
                              <img src={addPlus} alt="Add" />
                            </button>

                            <button
                              className={`minus-button ${adjustmentType === 0 ? "active" : ""}`}
                              onClick={() => setAdjustmentType(0)}
                            >
                              <img src={minusIcon} alt="Minus" />
                            </button>

                            <input
                              type="text"
                              value={adjustmentValue}
                              onChange={handleAdjustmentChange}
                              maxLength={FIELD_MAX_LENGTHS.ajustment}
                            />
                          </div>

                          <div className="value-split">
                            <img src={equalIcon} alt="Equals" />
                            <p className="modal-value">
                              {finalWorkDone} {unitLabel}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="m-button">
                      <button
                        className="modal-submit button-width1 mx-auto"
                        onClick={handleSubmit}
                        disabled={isLoading}
                      >
                        {isLoading ? <Loader /> : "Update"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdjustTons;
